import React, { useRef, useState } from "react";
import { motion, useMotionValue, useSpring, useTransform } from "framer-motion";
import { Download, Maximize2, Loader2 } from "lucide-react";

export const downloadImage = async (src, name, onState) => {
  onState?.(true);
  try {
    const res = await fetch(src, { mode: "cors" });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${name.replace(/[^\w\u0980-\u09FF]+/g, "-").toLowerCase()}.jpg`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch {
    window.open(src, "_blank", "noopener");
  } finally {
    onState?.(false);
  }
};

const GalleryCard = ({ item, title, tx, onOpen, onHover }) => {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const mx = useMotionValue(0.5);
  const my = useMotionValue(0.5);
  const sx = useSpring(mx, { stiffness: 180, damping: 22 });
  const sy = useSpring(my, { stiffness: 180, damping: 22 });
  const rotateX = useTransform(sy, [0, 1], [8, -8]);
  const rotateY = useTransform(sx, [0, 1], [-8, 8]);
  const imgX = useTransform(sx, [0, 1], ["-4%", "4%"]);
  const imgY = useTransform(sy, [0, 1], ["-4%", "4%"]);
  const glareX = useTransform(sx, [0, 1], ["0%", "100%"]);
  const glareY = useTransform(sy, [0, 1], ["0%", "100%"]);

  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width);
    my.set((e.clientY - r.top) / r.height);
    onHover?.(e, true);
  };
  const onLeave = () => { mx.set(0.5); my.set(0.5); onHover?.(null, false); };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, scale: 0.9, y: 30 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      exit={{ opacity: 0, scale: 0.9, y: 20 }}
      transition={{ duration: 0.45, ease: [0.22, 1, 0.36, 1] }}
      className="mb-5 break-inside-avoid"
      style={{ perspective: 1000 }}
      data-testid={`gallery-item-${item.id}`}
    >
      <motion.div
        ref={ref}
        onMouseMove={onMove}
        onMouseLeave={onLeave}
        onClick={() => onOpen(item)}
        style={{ rotateX, rotateY, transformStyle: "preserve-3d" }}
        className={`group relative overflow-hidden rounded-2xl bg-jannah-green-dark shadow-md hover:shadow-2xl cursor-none ${item.tall ? "aspect-[3/4]" : "aspect-[4/3]"}`}
      >
        <motion.img
          src={item.src}
          alt={title}
          loading="lazy"
          style={{ x: imgX, y: imgY, scale: 1.12 }}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-[1.18]"
        />
        <motion.div
          style={{ background: "radial-gradient(circle at var(--gx) var(--gy), rgba(255,255,255,0.28), transparent 55%)", "--gx": glareX, "--gy": glareY }}
          className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none mix-blend-overlay"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-jannah-green-dark/90 via-jannah-green-dark/10 to-transparent opacity-70 group-hover:opacity-95 transition-opacity" />
        <div className="absolute inset-x-0 bottom-0 p-5 translate-y-3 group-hover:translate-y-0 transition-transform duration-500" style={{ transform: "translateZ(40px)" }}>
          <div className="text-white font-bold text-lg leading-tight drop-shadow">{title}</div>
          <div className="mt-3 flex gap-2 opacity-0 group-hover:opacity-100 transition-opacity duration-300 delay-100">
            <button
              onClick={(e) => { e.stopPropagation(); onOpen(item); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md bg-white/15 backdrop-blur border border-white/25 text-white text-xs font-semibold hover:bg-white hover:text-jannah-green-dark transition-colors cursor-pointer"
              data-testid={`gallery-view-${item.id}`}
            >
              <Maximize2 size={13} /> {tx.view}
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); downloadImage(item.src, title, setBusy); }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full btn-gold text-xs font-semibold transition-colors cursor-pointer"
              data-testid={`gallery-download-${item.id}`}
            >
              {busy ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />} {busy ? tx.downloading : tx.download}
            </button>
          </div>
        </div>
        <span className="absolute top-4 left-4 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-[10px] font-bold uppercase tracking-widest">{item.cat}</span>
      </motion.div>
    </motion.div>
  );
};

export default GalleryCard;
